/* eslint-disable @typescript-eslint/no-use-before-define */
import axios, { AxiosResponse } from "axios";
import * as tus from "tus-js-client";

import { isDemo } from "@/utils";
import { QrCodeObject } from "@/models/QrCodeObject";
import getMarkerService from "@/services/MarkerService";
import Experience from "@/models/Experience";
// import customHomePage from "@/mocks/homePageCustom";

let instance: QrCodeService = null;

export class QrCodeService {
    async loadQrCode(qrId: string): Promise<QrCodeObject> {
        let qrcodeObject;

        /**
         * In STQR manager, the app is included in an iframe.
         * In this case, we ask for the QRCode to the parent window using postMessage.
         */
        if (isDemo() && window.parent.location !== window.location && window.parent.postMessage) {
            qrcodeObject = await new Promise(resolve => {
                const onMessage = message => {
                    try {
                        const json = JSON.parse(message.data);

                        if (json?.type === "qrcodeObject" && json?.value) {
                            window.removeEventListener("message", onMessage);
                            resolve(json.value);
                        }
                    } catch {
                        return;
                    }
                };
                window.addEventListener("message", onMessage);
                window.parent.postMessage("getQrCode", "*");
            });
        } else {
            /**
             * Otherwise, simply ask to the API
             */
            const response = await axios.get(`qrcodes/${qrId}`);
            qrcodeObject = response.data;
        }
        qrcodeObject.isOpen = qrcodeObject.experiences.length === 0;

        // qrcodeObject.htmlPage = customHomePage;

        console.log("found QRCode");
        if (window.stqr === undefined) {
            window.stqr = {};
        }
        window.stqr.qrcode = qrcodeObject;
        const event = new CustomEvent("qrcode", {
            detail: qrcodeObject
        });
        window.dispatchEvent(event);

        return qrcodeObject;
    }

    async logScan(qrcodeId: string) {
        const response = await axios.post(`stats/scan`, {
            qrcodeId
        });

        if (response.status === 200) {
            return response.data;
        }
    }

    public async computeVideoUrl(qrcodeObject: QrCodeObject): Promise<string> {
        // if content banned, simply return empty string
        if (qrcodeObject.xpBanned || qrcodeObject.projectBanned) {
            return "";
        }

        // use experience video if available
        if (qrcodeObject.experiences.length > 0) {
            const urlEnd = qrcodeObject.experiences[0].assets[0].data;
            return this.getFullVideoPath(urlEnd);
        } else if (qrcodeObject.defaultVideoUrl?.length > 0) {
            // otherwise use from defaultVideoUrl field if available
            return qrcodeObject.defaultVideoUrl;
        } else if (qrcodeObject.pageId > 1) {
            // // otherwise use video from assets
            try {
                const marker = await getMarkerService().loadMarker(qrcodeObject.pageId);
                const videoAsset = marker.assets.find(asset => asset.name === "replace_1_hide");
                return videoAsset
                    ? process.env.VUE_APP_BEAR_API_MEDIA + videoAsset.s1
                    : "https://media.stqr.io/videos/f291e3a9-0a1d-43cf-a841-6bfc6b3b1896.mp4.mp4";
            } catch {
                return "https://media.stqr.io/videos/f291e3a9-0a1d-43cf-a841-6bfc6b3b1896.mp4.mp4";
            }
        } else {
            return "https://media.stqr.io/videos/f291e3a9-0a1d-43cf-a841-6bfc6b3b1896.mp4.mp4";
        }
    }

    public computeCustomHtmlContent(
        qrcodeObject: QrCodeObject
    ): { html: string; scripts: string[] } {
        const result = {
            html: "",
            scripts: []
        };
        if (qrcodeObject.htmlPage?.length > 0 && qrcodeObject.htmlPage[0] === "<") {
            // extract javascript if necessary
            const jsRegex = RegExp(/<script.*?>([\n\s\w\W\d\D]+?)<\/script>/, "i");
            const remoteJsRegex = RegExp(/<script.*?src=["'](.+?)["'].*?><\/script>/, "i");

            let htmlContent = qrcodeObject.htmlPage;
            let match;
            while ((match = remoteJsRegex.exec(htmlContent)) !== null) {
                htmlContent =
                    htmlContent.substr(0, match.index - 1) +
                    htmlContent.substr(match.index + match[0].length);
                result.scripts.push(match[1]);
            }

            while ((match = jsRegex.exec(htmlContent)) !== null) {
                htmlContent =
                    htmlContent.substr(0, match.index - 1) +
                    htmlContent.substr(match.index + match[0].length);
                result.scripts.push(match[1]);
            }

            result.html = htmlContent;
        }

        return result;
    }

    public setExperienceAssets(qrcodeObject: QrCodeObject, videoUrl: string): void {
        if (!qrcodeObject) {
            console.error("trying to setExperience on not set QRCodeObject");
            return;
        }
        if (!qrcodeObject.isOpen) {
            console.log("trying to setExperience on not open QRCode");
            return;
        }

        // Argo asset with default video
        const defaultAsset = {
            type: 2,
            bearid: 0,
            data: videoUrl
        };
        const assets = [defaultAsset];

        if (!qrcodeObject.experiences.length) {
            const exp: Experience = {
                name: "",
                ownerId: "notSet",
                qrcodeId: qrcodeObject.qrcodeId,
                assets
            };
            qrcodeObject.experiences.push(exp);
        } else {
            qrcodeObject.experiences[0].assets = assets;
        }
    }

    getFullVideoPath(urlEnd: string): string {
        let fullPath: string;
        if (urlEnd.startsWith("media/")) {
            fullPath = process.env.VUE_APP_BEAR_API_MEDIA + urlEnd;
        } else {
            fullPath = process.env.VUE_APP_MEDIA_ROOT + "videos/" + urlEnd;
        }
        return fullPath;
    }

    async saveExperience(
        video: File,
        name: string,
        qrcodeObject: QrCodeObject,
        userData: {
            firstName?: string;
            lastName?: string;
            email?: string;
            phone?: string;
            videoAgreement?: boolean;
        } = {},
        onProgress: (progression: number) => void
    ): Promise<AxiosResponse> {
        /**
         * do not allow to save experience in demo mode or on master qrCode
         */
        if (isDemo()) {
            return null;
        }
        try {
            const videoUrl = await videoPartialUpload(video, onProgress);

            const formData = new FormData();
            formData.append("video", videoUrl);
            formData.append("name", name);

            Object.keys(userData).forEach(key => {
                if (userData[key] && userData[key].toString().length > 0) {
                    formData.append(key, userData[key]);
                }
            });

            const response = await axios.post(
                `experiences/video/${qrcodeObject.qrcodeId}`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                }
            );
            return response;
        } catch (e) {
            console.error(e);
        }
    }
}

async function videoPartialUpload(file: File, onProgressCb: (progression: number) => void): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const upload = new tus.Upload(file, {
            endpoint: `${process.env.VUE_APP_API_ROOT}uploads`,
            retryDelays: [0, 3000, 5000, 10000, 20000],
            chunkSize: 5 * 1024 * 1024,
            metadata: {
                filename: file.name,
                filetype: file.type
            },
            onError: (error: Error) => {
                console.error("Video upload failed because: " + error);
                reject(error);
            },
            onProgress: (bytesUploaded, bytesTotal) => {
                onProgressCb(Number(((bytesUploaded / bytesTotal) * 100).toFixed(2)));
            },
            onSuccess: () => {
                if (upload.url) {
                    resolve(upload.url);
                }
            }
        });

        // Check if there are any previous uploads to continue.
        upload.findPreviousUploads().then(previousUploads => {
            // Found previous uploads so we select the first one.
            if (previousUploads.length) {
                upload.resumeFromPreviousUpload(previousUploads[0]);
            }

            upload.start();
        });
    });
}

export default function getInstance(): QrCodeService {
    if (!instance) {
        instance = new QrCodeService();
    }

    return instance;
}
